import { Ruler3D } from 'features/assetViz/objects/decor/Ruler3D'
import { cartesianCircleGeometry as geometry } from 'features/assetViz/utils/geometryDefines'
import { extractScale, Vec3 } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoFlatCircularFloor,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'
/**
 * A surface representing a flat circular floor, plotted on a cartesian plane,
 * with the center of its base at "position".
 * @param model               The flat circular floor component model
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const FlatCircularFloorSurface3D: React.FC<
  {
    model: GeckoFlatCircularFloor
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { bounds, scale } = React.useMemo(
    () => ({
      bounds: model.componentBounds,
      scale: extractScale(groupProps.scale).multiply(
        new Vector3(
          model.radius.scale(2.0).num,
          model.radius.scale(2.0).num,
          1.0
        )
      ),
    }),
    [model, groupProps.scale]
  )

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(model.componentBounds.start.x)
        .scale(1.0 / model.componentBounds.width.num).num
      return [relX - 0.5, -0.6, 0] // offset ruler by -1
    },
    [model.componentBounds]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(model.componentBounds.start.y)
        .scale(1.0 / model.componentBounds.height.num).num
      return [-0.6, relY - 0.5, 0] // offset ruler by -1
    },
    [model.componentBounds]
  )

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      // Apply the true scaling last (override anything in groupProps)
      scale={scale}>
      <Ruler3D
        anchor={'right'}
        start={model.componentBounds.start.y}
        end={model.componentBounds.end.y}
        includeTicks
        includeStart
        includeEnd
        getTickPosition={positionYTick}
      />
      <Ruler3D
        anchor={'top'}
        start={model.componentBounds.start.x}
        end={model.componentBounds.end.x}
        includeTicks
        includeStart
        includeEnd
        getTickPosition={positionXTick}
      />
    </ModelSurface3D>
  )
}
